import { Icon } from 'icons';
import { Button } from '@/components/atoms/Button/Button';
import { useEffect, useState, KeyboardEvent } from 'react';
import * as styles from '@/components/organisms/Table/Table.css';
import * as textarea from '@/components/atoms/Input/Input.css';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { academyDashboardApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';
import { formatNumber } from '@/utils/number';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface StatCard {
    id: string;
    title: string;
    value: string;
    unit: string;
    icon: string;
}

interface ContentData {
    id: number;
    category: string;
    creator: string;
    createdAt: string;
    sequence: number;
}

interface LearningContent {
    id: string;
    title: string;
    value: string;
    unit: string;
    icon: string;
    colSpan: number;
}

interface ServiceMessage {
    icon: string;
    title: string;
    buttons: {
        text: string;
        intent?: 'primary' | 'secondary';
        onClick?: () => void;
    }[];
}

const normalizeAcademyRole = (role?: string | null) => {
    const normalized = role?.toString().trim().toLowerCase() || '';
    if (normalized.includes('teacher')) return 'teacher';
    if (normalized.includes('owner') || normalized.includes('academy')) return 'owner';
    return normalized || 'owner';
};

export default function AcademyHome() {
    const [isPaid, setIsPaid] = useState(false);
    const [withdrawDialog, setWithdrawDialog] = useState(false);
    const navigate = useNavigate();
    const slugPrefix = getSlugPrefix();
    const { role: sessionRole } = useSessionSlug();
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const effectiveRole = normalizeAcademyRole(sessionRole || storedRole || '');
    const isTeacher = effectiveRole === 'teacher';
    const goPlanPage = () => navigate(`${slugPrefix}/academy/mypage/plan`);
    const goExamManagementPage = () => {
        // [QA Fix] 잔여 응시권 클릭 시 시험지 관리 화면으로 이동
        navigate(`${slugPrefix}/academy/paper/list`);
    };
    const handleExamTicketKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            goExamManagementPage();
        }
    };
    
    // 서비스 상태 타입 정의
    type ServiceStatus = 'normal' | 'trial_ended_owner';
    const [serviceStatus, setServiceStatus] = useState<ServiceStatus>('normal');
    const [statCards, setStatCards] = useState<StatCard[]>([
        { id: 'total-students', title: '전체 수강생 수', value: '0', unit: '명', icon: 'person' },
        { id: 'today-students', title: '오늘 접속한 학생수', value: '0', unit: '명', icon: 'enter' },
        { id: 'exam-tickets', title: '사용가능한 시험 응시권', value: '0', unit: '개', icon: 'ticket' },
    ]);

    // 오늘 콘텐츠 업데이트 이력
    const [data, setData] = useState<ContentData[]>([]);

    const columns = [
        { key: 'sequence', render: (row: ContentData) => formatNumber(row.sequence) },
        { key: 'category', render: (row: ContentData) => row.category },
        { key: 'creator', render: (row: ContentData) => row.creator },
        { key: 'createdAt', render: (row: ContentData) => row.createdAt }
    ];

    const handleRowClick = () => {
        console.log('Row clicked');
    };

    // 사용 가능한 학습 콘텐츠
    const [learningContents, setLearningContents] = useState<LearningContent[]>([
        { id: 'words', title: '단어', value: '0', unit: '개', icon: 'word', colSpan: 3 },
        { id: 'wordbooks', title: '단어장', value: '0', unit: '개', icon: 'bookA', colSpan: 1 },
        { id: 'units', title: '유닛', value: '0', unit: '개', icon: 'block', colSpan: 1 },
        { id: 'textbooks', title: '교재', value: '0', unit: '개', icon: 'book', colSpan: 1 },
    ]);

    // 이번달 결제 완료 금액
    const [monthlyPaidAmount, setMonthlyPaidAmount] = useState<number>(0);

    // 초기 데이터 로딩
    useEffect(() => {
        const loadSummary = async () => {
            try {
                const res = await academyDashboardApi.summary();
                console.log('[AcademyHome] API 전체 응답:', res);
                
                // ApiResponse 구조: { success, data, message }
                const summary = res?.data || {};
                console.log('[AcademyHome] summary 데이터:', summary);

                // 상단 카드
                setStatCards([
                    { id: 'total-students', title: '전체 수강생 수', value: formatNumber(summary.totalStudents ?? 0), unit: '명', icon: 'person' },
                    { id: 'today-students', title: '오늘 접속한 학생수', value: formatNumber(summary.todayActiveStudents ?? 0), unit: '명', icon: 'enter' },
                    { id: 'exam-tickets', title: '사용가능한 시험 응시권', value: formatNumber(summary.availableTickets ?? 0), unit: '개', icon: 'ticket' },
                ]);

                // 업데이트 이력
                const updates: ContentData[] = (summary.contentUpdates || []).map((r: any) => ({
                    id: Number(r.id || 0),
                    category: String(r.category || ''),
                    creator: String(r.creator || ''),
                    createdAt: r.createdAt ? String(r.createdAt).replace('T', ' ').substring(0, 16) : '',
                    sequence: 0,
                }));
                const updatesWithSequence: ContentData[] = updates.map((item: ContentData, idx: number, arr: ContentData[]) => ({
                    ...item,
                    sequence: arr.length - idx,
                }));
                setData(updatesWithSequence);

                // 학습 콘텐츠 카운트
                const counts = summary.contentCounts || {};
                setLearningContents([
                    { id: 'words', title: '단어', value: formatNumber(counts.words ?? 0), unit: '개', icon: 'word', colSpan: 3 },
                    { id: 'wordbooks', title: '단어장', value: formatNumber(counts.wordbooks ?? 0), unit: '개', icon: 'bookA', colSpan: 1 },
                    { id: 'units', title: '유닛', value: formatNumber(counts.units ?? 0), unit: '개', icon: 'block', colSpan: 1 },
                    { id: 'textbooks', title: '교재', value: formatNumber(counts.textbooks ?? 0), unit: '개', icon: 'book', colSpan: 1 },
                ]);

                // 이번달 결제 금액
                setMonthlyPaidAmount(isTeacher ? 0 : Number(summary.monthlyPaidAmount ?? 0));
            } catch (e) {
                console.error('[AcademyHome] 대시보드 로딩 실패', e);
            }
        };
        loadSummary();
    }, [isTeacher]);

    // 서비스 메시지 데이터
    const serviceMessages: Record<ServiceStatus, ServiceMessage> = {
        normal: {
            icon: 'info',
            title: '서비스 개설비용 납부가 필요합니다.\n자세한 내용은 알파영어에 문의 해보세요.\n문의: 010-3203-5985', // [QA Fix] 서비스 개설비 연락처 노출
            buttons: []
        },
        trial_ended_owner: {
            icon: 'info',
            title: '서비스 이용료 납부 후 시스템 사용이 가능합니다.',
            buttons: [
                { text: '탈퇴 신청하기', intent: 'secondary', onClick: () => setWithdrawDialog(true) },
                { text: '이용료 납부 바로가기', intent: 'primary', onClick: goPlanPage }
            ]
        }
    };
    return (
        <main>
            {/* 서비스 상태에 따른 조건부 렌더링 */}
            
            {/* 서비스 메시지 화면 */}
            {serviceStatus !== 'normal' && (
                <section className='flex flex-col gap-5 justify-center items-center my-10'>
                    <Icon name={serviceMessages[serviceStatus].icon} size={120} />
                    <p className='text-lg font-bold text-center text-gray-900 whitespace-pre-line'>
                        {serviceMessages[serviceStatus].title}
                    </p>
                    {serviceMessages[serviceStatus].buttons.length > 0 && (
                        serviceMessages[serviceStatus].buttons.length === 1 ? (
                            <Button className='w-full max-w-[360px]'>
                                {serviceMessages[serviceStatus].buttons[0].text}
                            </Button>
                        ) : (
                            <div className='flex gap-2 max-w-[500px] w-full'>
                                {serviceMessages[serviceStatus].buttons.map((button, index) => (
                                    <Button 
                                        key={index}
                                        intent={button.intent} 
                                        className='w-full'
                                        onClick={button.onClick}
                                    >
                                        {button.text}
                                    </Button>
                                ))}
                            </div>
                        )
                    )}
                </section>
            )}

            {/* 서비스 비용 납부 안했을 때 메인 화면 */}
            {serviceStatus === 'normal' && isPaid && (
                <section className='flex flex-col gap-5 justify-center items-center'>
                    <Icon name={serviceMessages.normal.icon} size={120} />
                    <p className='text-lg font-bold text-center text-gray-900 whitespace-pre-line'>
                        {serviceMessages.normal.title}
                    </p>
                </section>
            )}

            {/* 정상 서비스 화면 */}
            {serviceStatus === 'normal' && !isPaid && (
                    <section className='my-10'>
                        <ul className='grid grid-cols-3 gap-4'>
                            {statCards.map((card) => {
                                const isExamTicketCard = card.id === 'exam-tickets';
                                return (
                                    <li
                                        key={card.id}
                                        className={`flex justify-between items-end p-5 bg-gray-50 rounded-lg border border-gray-100 dash-shadow ${isExamTicketCard ? 'cursor-pointer' : ''}`}
                                        role={isExamTicketCard ? 'button' : undefined}
                                        tabIndex={isExamTicketCard ? 0 : undefined}
                                        onClick={isExamTicketCard ? goExamManagementPage : undefined}
                                        onKeyDown={isExamTicketCard ? handleExamTicketKeyDown : undefined}
                                        aria-label={isExamTicketCard ? '시험지 관리 화면으로 이동' : undefined}
                                    >
                                        <div className='flex flex-col items-start'>
                                            <Icon name={card.icon} size={24} />
                                            <p className='text-lg font-bold text-gray-600'>{card.title}</p>
                                        </div>
                                        <p className='flex gap-2 items-end text-4xl font-extrabold text-indigo-500'>
                                            {card.value}
                                            <span className='pb-1 text-lg text-gray-500'>{card.unit}</span>
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className='grid grid-cols-2 gap-10 my-10'>
                            {/* 테이블 */}
                            <div  className='flex flex-col col-span-1 gap-4'>
                                <h1 className='heading1'>오늘 콘텐츠 업데이트 이력</h1>
                                {/* dash-shadow */}
                                <div className='overflow-hidden rounded-2xl border border-gray-200 lg:gap-0'>
                                <ul className={`${styles.headerList} w-full`}>
                                <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                                    <li className={`${styles.headerItem}`}>분류</li>
                                    <li className={`${styles.headerItem} max-w-[200px]`}>생성자</li>
                                    <li className={`${styles.headerItem} max-w-[200px]`}>생성일시</li>
                                </ul>
                                    <div className='flex overflow-hidden flex-col w-full'>
                                        {data.length === 0 ? (
                                            <div className='flex justify-center items-center py-10 text-sm text-gray-500 border-t border-gray-100'>
                                                금일 등록된 컨텐츠가 없습니다.
                                            </div>
                                        ) : (
                                            data.map((row) => (
                                            <ul 
                                                key={row.id} 
                                                className={`${styles.bodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                                                onClick={handleRowClick}
                                            >
                                            {columns.map(({ key, render }) => (
                                                <li
                                                key={key}
                                                className={`${styles.bodyItem} ${
                                                    key === 'id' ? 'max-w-[80px] hidden lg:flex justify-center' :
                                                    key === 'category' ? 'col-span-2 lg:col-span-1 ' :
                                                    key === 'creator' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                                    key === 'createdAt' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                                    ''
                                                }`}
                                                >
                                                {render(row)}
                                                </li>
                                            ))}
                                            </ul>
                                            ))
                                        )}
                                    </div>
                                </div>


                            </div>
                            <div  className='flex flex-col gap-4'>
                            <h1 className='heading1'>사용 가능한 학습 콘텐츠</h1>
                                <div>
                                    <ul className='grid grid-cols-3 gap-3'>
                                        {learningContents.map((content) => (
                                            <li key={content.id} className='w-full' style={{ gridColumn: `span ${content.colSpan}` }}>
                                                <div className='p-5 bg-gray-50 rounded-2xl border border-indigo-100 dash-shadow'>
                                                    <div className='flex justify-center items-center p-1 mt-1 bg-indigo-50 rounded-sm size-7'>
                                                        <Icon name={content.icon} size={20} className='*:fill-indigo-500'/>
                                                    </div>
                                                    <p className='text-lg font-bold text-gray-500'>{content.title}</p>
                                                    <p className='flex gap-2 items-end text-4xl font-extrabold text-gray-900'>{content.value}<span className='pb-1 text-lg text-gray-500'>{content.unit}</span></p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {!isTeacher && (
                            <div  className='flex flex-col gap-4'>
                            <h1 className='heading1'>이번달 결제 완료 금액</h1>
                            <div className='p-5 bg-gray-50 rounded-2xl border border-indigo-100 dash-shadow'>
                                        <p className='text-4xl font-extrabold text-gray-900'>{formatNumber(monthlyPaidAmount)} <span className='text-lg'>원</span></p>
                                        <Link to='/academy/mypage/paymentHistory' className='flex'>
                                            <Button intent='secondary' text size='small' className='flex !pl-0'>
                                                <span> 결제 이력 보러가기</span>
                                                <Icon name='arrowDown' size={21} className='*:fill-gray-500 rotate-270 ' />
                                            </Button>
                                        </Link>
                                    

                            </div>
                            </div>
                        )}
                    </section>
            )}



            {/* 탈퇴 신청 다이얼로그 */}
            {withdrawDialog && (
                <Dialog>
                    <div className='p-5 pb-0'>
                        <p className='mb-2 text-lg font-bold text-gray-900'>탈퇴사유를 입력해주세요</p>
                        <textarea className={`${textarea.input} w-full resize-none`} rows={5} placeholder='탈퇴사유를 입력해주세요' />
                        <span className='block text-sm text-center text-gray-500'>알파영어의 승인 후 탈퇴가 처리됩니다.</span>
                    </div>
                </Dialog>
            )}
        </main>
    )
}